import { Input, Button, Upload, Switch, Flex, ConfigProvider, theme, Select } from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { DatasetImporterTextControls } from "../../ReteControls/DatasetImporterTextControl";
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import DatasetViewerComponent from "./DatasetViewerComponent";
import "../../../styles/DatasetImporter.css";
import { debouncedAutoSave } from "../../editor";
import { axiosInstance } from "../../../helpers/nodeHelpers";
import { HistoryExtensions } from "rete-history-plugin";
import { BACKEND_URL } from "../../../config";
import { saveGraph } from "../../../helpers/importExportFunctions";
import { DefaultOptionType } from "antd/es/select";

const isTypeCsv = (file: UploadFile) => {
    return (
        file.type === "text/csv" ||
        file.type === "text/plain" ||
        file.type === "application/csv" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/x-csv"
    );
};

const delayFunction = <F extends (...args: any[]) => void>(
    fn: F,
    ms: number
): ((...args: Parameters<F>) => void) => {
    let timer: NodeJS.Timeout | null = null;

    return function (...args: Parameters<F>) {
        clearTimeout(timer as NodeJS.Timeout);
        timer = setTimeout(() => {
            fn(...(args as Parameters<F>));
        }, ms);
    };
};

const delayUrlLog = delayFunction(
    (type: string, context: any) => debouncedAutoSave(type, context),
    1000
);

type Dataset = {
    id: number;
    dataset_name: string;
    dataset_path: string;
    createdAt: string;
    last_accessed_at: string;
};

const COMPONENT_ACTION_ID = "importDataset";

export const DatasetImporterComponent = (props: { data: DatasetImporterTextControls }) => {
    const [text, setText] = useState<string>(props.data.value);
    const [switchStatus, setSwitchStatus] = useState<boolean>(!props.data.useUploadedDataset);
    const [file, setFile] = useState<File | null>(null);
    const [fileUploading, setFileUploading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<boolean>(false);
    const [savedFile, setSavedFile] = useState<UploadFile[] | null>(null);
    const [datasets, setDatasets] = useState<Dataset[]>([]);
    const [recentDatasets, setRecentDatasets] = useState<Dataset[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    const context = props.data.context;

    const toggleUrlImportStep = (action: string) => {
        if (props.data.isValidCsvUrl(action)) {
            context.markTaskAsCompleted(COMPONENT_ACTION_ID);
        } else {
            context.removeTaskFromCompleted(COMPONENT_ACTION_ID);
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        props.data.onChange(e.target.value);
        setText(e.target.value);
        toggleUrlImportStep(e.target.value);
        delayUrlLog("enter_url_download", {
            url_download: true,
            currentURL: e.target.value,
        });
    };

    const handleFileRead = (file: RcFile, skipLog = false): void => {
        setFileUploading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            const content = e.target?.result;
            if (typeof content === "string") {
                setFile(file);
                props.data.uploadDataset(content, file.name);
            }
        };
        reader.readAsText(file);
        if (!skipLog) {
            debouncedAutoSave("upload_local_dataset", { fileName: file.name, url_download: false });
        }
    };

    const beforeUpload = async (file: RcFile, fileList: UploadFile[], skipUpload = false) => {
        // Check if there's an existing file, update date accessed on that file
        if (savedFile && savedFile.length > 0) {
            await UpdateDateDatasetAccessedByFile(savedFile[0]);
        }
        if (!isTypeCsv(file)) {
            fileList[0].status = "error";
            return false;
        }
        if (!skipUpload) {
            setSavedFile(fileList);
            await uploadFileToServer(file);
        }
        handleFileRead(file, skipUpload);
        if (context) {
            const { markTaskAsCompleted } = context;
            markTaskAsCompleted(COMPONENT_ACTION_ID);
        }
        return false;
    };

    const onRemove = async (file: UploadFile) => {
        await UpdateDateDatasetAccessedByFile(file);
        setFile(null);
        setSavedFile(null);
        setErrorMessage(false);
        props.data.setDatasetColumns([]);
        context.removeTaskFromCompleted(COMPONENT_ACTION_ID);
        debouncedAutoSave("remove_local_dataset", { fileName: file.name, url_download: false });
        props.data.uploadDataset("", null);

        return true;
    };

    const UpdateDateDatasetAccessedByFile = async (file: UploadFile) => {
        const dataset = recentDatasets.find((d) => d.dataset_name === file.name);
        if (dataset) {
            await updateDateDatasetAccessed(dataset);
        }
    };

    const getElementTotalHeight = (el: HTMLElement) => {
        const styles = window.getComputedStyle(el);
        const margin = parseFloat(styles["marginTop"]) + parseFloat(styles["marginBottom"]);
        return el.offsetHeight + margin;
    };

    const sumImmediateChildrenHeights = (parentDiv: HTMLElement) => {
        let totalHeight = 0;
        for (let child of parentDiv.children) {
            totalHeight += getElementTotalHeight(child as HTMLElement);
        }
        return totalHeight;
    };

    const getCurrentHeight = () => {
        if (!containerRef.current) return 295;

        const controlContainer: HTMLElement = containerRef.current;
        const overallNodeContainer: HTMLElement =
            controlContainer.closest("[data-testid='node']") || document.createElement("div")!;
        const totalRequiredHeight = sumImmediateChildrenHeights(overallNodeContainer);
        // Clear any hardcoded dimensions from the auto arrange plugin
        overallNodeContainer.style.height = "";
        overallNodeContainer.style.width = "";
        return totalRequiredHeight + 5;
    };

    const onSwitch = (checked: boolean) => {
        props.data.setDatasetSource(!checked);
        setSwitchStatus(checked);
        setErrorMessage(false);
        if (checked) {
            toggleUrlImportStep(text);
        } else if (!file) {
            context.removeTaskFromCompleted(COMPONENT_ACTION_ID);
        }
        debouncedAutoSave("toggle_url_download_switch", {
            url_download: checked,
            current_url: text,
            current_file: file?.name || "No file uploaded",
        });
    };

    const uploadFileToServer = async (file: File) => {
        const formData = new FormData();
        try {
            formData.append("file", file);
            const res = await axiosInstance.post("/api/file/upload", formData);
            setErrorMessage(false);
        } catch (error: any) {
            console.log(error);
            if (error.response.status === 413) {
                console.log("File too large to save");
                setErrorMessage(true);
                return true;
            }
        }
        return false;
    };

    const getUploadedDatasetPath = async (fileName: string) => {
        try {
            const res = await axiosInstance.get(`/api/file/get-path/${fileName}`);
            return res.data.dataset;
        } catch (error: any) {
            console.log(error);
            return null;
        }
    };

    const updateDateDatasetAccessed = async (dataset: Dataset) => {
        try {
            await axiosInstance.post(`/api/file/update-dataset-last-accessed-at`, {
                id: dataset.id,
            });
        } catch (error) {
            console.log("Failed to update dataset accessed date");
        }
    };

    const loadDatasetIntoEditor = async (dataset: Dataset, recentDataset = false) => {
        try {
            const res = await axiosInstance.get(`${BACKEND_URL}${dataset.dataset_path}`, {
                responseType: "blob",
            });
            const blob = res.data;
            const file = new File([blob], dataset.dataset_name, {
                type: blob.type,
            });
            const tempUploadFile: UploadFile = {
                uid: "-1",
                name: dataset.dataset_name,
                status: "done",
            };

            setSavedFile([tempUploadFile]);
            await beforeUpload(file as RcFile, [file as RcFile], true);
            if (recentDataset) {
                await updateDateDatasetAccessed(dataset);
                debouncedAutoSave("load_recent_dataset", {
                    fileName: dataset.dataset_name,
                    url_download: false,
                });
            }
        } catch (error: any) {
            console.log("Failed to retrieve dataset path, resorting to default behavior");
        }
    };

    const setupForPreviouslyUploadedDataset = async () => {
        const dataset: Dataset | null = await getUploadedDatasetPath(
            props.data.uploadedDataset.fileName
        );
        if (dataset) {
            await loadDatasetIntoEditor(dataset);
        } else {
            console.log("Failed to retrieve dataset path, resorting to default behavior");
        }
    };

    const getRecentlyUploadedDatasets = async () => {
        try {
            const res = await axiosInstance.get("/api/file/recently-uploaded-datasets");
            const datasets = res.data.datasets;
            if (!datasets) {
                setDatasets([]);
                setRecentDatasets([]);
                return;
            }
            setDatasets(datasets);
            const updatedDatasets = datasets.map((dataset: Dataset) => {
                const timeSinceLastAccessed = formatTimeSinceLastAccessed(dataset.last_accessed_at);
                return {
                    ...dataset,
                    last_accessed_at: timeSinceLastAccessed,
                };
            });

            setRecentDatasets(updatedDatasets);
        } catch (error: any) {
            console.log(error);
        }
    };

    const formatTimeSinceLastAccessed = (last_accessed_at: string) => {
        const now = new Date();
        const lastAccessed = new Date(last_accessed_at);
        const diffInMs = now.getTime() - lastAccessed.getTime(); // Difference in milliseconds
        const diffInSecs = Math.floor(diffInMs / 1000);
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInSecs < 2) {
            return "just now";
        } else if (diffInSecs < 60) {
            // Less than a minute, show seconds
            return `${diffInSecs} second${diffInSecs === 1 ? "" : "s"} ago`;
        } else if (diffInMinutes < 120) {
            // Less than 2 hours, show minutes
            return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
        } else if (diffInHours < 24) {
            // Less than 24 hours, show hours and minutes
            const remainingMinutes = diffInMinutes % 60;
            return remainingMinutes === 0 || diffInHours > 2
                ? `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`
                : `${diffInHours} hour${
                      diffInHours === 1 ? "" : "s"
                  } and ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"} ago`;
        } else {
            // 24 hours or more, show days
            return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
        }
    };

    const handleDeleteRecentDataset = async (e: React.MouseEvent, deleted_dataset: Dataset) => {
        e.stopPropagation();
        const { id, dataset_name } = deleted_dataset;
        try {
            const res = await axiosInstance.post(`/api/file/delete-dataset`, {
                id,
            });
            if (res.status === 200) {
                setDatasets(datasets.filter((dataset) => dataset.id !== id));
                setRecentDatasets(recentDatasets.filter((dataset) => dataset.id !== id));
                debouncedAutoSave("delete_recent_dataset", {
                    dataset_id: id,
                    fileName: dataset_name,
                    url_download: false,
                });
            }
        } catch (error) {
            console.log("Failed to delete dataset on backend");
        }
    };

    useEffect(() => {
        if (containerRef.current) {
            const overalDatasetImporter: HTMLElement =
                containerRef.current.closest("[data-testid='node']") ||
                document.createElement("div");
            // Reset hardcoded dimensions from auto arrange plugin
            overalDatasetImporter.style.width = "";
            overalDatasetImporter.style.height = "";
        }
    }, [file]);

    useEffect(() => {
        if (props.data.useUploadedDataset && props.data.uploadedDataset.fileName !== "") {
            setupForPreviouslyUploadedDataset();
        }
    }, []);

    useEffect(() => {
        if (!savedFile) {
            getRecentlyUploadedDatasets();
        }
    }, [savedFile]);

    useEffect(() => {
        if (datasets.length > 0) {
            const interval = setInterval(() => {
                setRecentDatasets(
                    datasets.map((dataset: Dataset) => {
                        const timeSinceLastAccessed = formatTimeSinceLastAccessed(
                            dataset.last_accessed_at
                        );
                        return {
                            ...dataset,
                            last_accessed_at: timeSinceLastAccessed,
                        };
                    })
                );
            }, 60000);
            return () => clearInterval(interval);
        }
    }, [datasets]);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === containerRef.current) {
                    const height = getCurrentHeight();
                    props.data.adjustHeight(height);
                }
            }
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        // Cleanup observer on unmount
        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <ConfigProvider
            theme={{
                algorithm: theme.darkAlgorithm,
            }}
        >
            <Flex
                gap={"small"}
                align="center"
                vertical={true}
                ref={containerRef}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <Upload
                    accept=".csv"
                    listType="text"
                    maxCount={1}
                    beforeUpload={beforeUpload}
                    onRemove={onRemove}
                    disabled={switchStatus}
                    fileList={savedFile || undefined}
                    className="fileUploadContainer"
                >
                    <Button disabled={switchStatus} icon={<UploadOutlined />}>
                        Upload dataset (csv)
                    </Button>
                </Upload>
                {recentDatasets.length > 0 && !savedFile && (
                    <Select
                        defaultValue={"Recent Datasets"}
                        dropdownStyle={{ width: "fit-content" }}
                        disabled={switchStatus}
                        onSelect={(path: string, option) =>
                            loadDatasetIntoEditor(
                                {
                                    id: option.key,
                                    dataset_name: option.dataset_name,
                                    dataset_path: path,
                                    createdAt: option.createdAt,
                                    last_accessed_at: option.last_accessed_at,
                                },
                                true
                            )
                        }
                        options={recentDatasets.map((dataset) => ({
                            key: dataset.id,
                            value: dataset.dataset_path,
                            dataset_name: dataset.dataset_name,
                            createdAt: dataset.createdAt,
                            last_accessed_at: dataset.last_accessed_at,
                            label: (
                                <div className="recentDatasetOptionOverallContainer">
                                    <div
                                        className="recentDatasetOptionNameAndDateAccessed"
                                        title={`Dataset: ${
                                            dataset.dataset_name
                                        }\nUploaded: ${new Date(
                                            dataset.createdAt
                                        ).toLocaleString()}\nLast accessed: ${
                                            dataset.last_accessed_at
                                        }`}
                                    >
                                        <span className="recentDatasetOptionName">
                                            {dataset.dataset_name}
                                        </span>
                                        <span className="recentDatasetOptionDate">
                                            Last accessed: {dataset.last_accessed_at}
                                        </span>
                                    </div>
                                    <Button
                                        className="recentDatasetOptionDeleteButton"
                                        type="text"
                                        icon={
                                            <CloseOutlined
                                                className="deleteRecentDatasetIcon"
                                                style={{ color: "#ff0000" }}
                                            />
                                        }
                                        title={`Remove from recent datasets`}
                                        onClick={(e) => handleDeleteRecentDataset(e, dataset)}
                                        aria-label={`Delete dataset: ${dataset.dataset_name}`}
                                    />
                                </div>
                            ),
                        }))}
                    ></Select>
                )}
                {errorMessage && (
                    <span className="fileUploadErrorMessage">
                        File too large to save, only available until refresh
                    </span>
                )}
                <span>OR</span>
                <Flex gap={"small"}>
                    <span>URL download?</span>
                    <Switch checked={switchStatus} onChange={onSwitch} disabled={fileUploading} />
                </Flex>
                <Input
                    disabled={!switchStatus}
                    placeholder="Enter dataset url"
                    value={text}
                    onChange={handleChange}
                    aria-label="Enter dataset URL"
                    name="datasetUrlInput"
                />
                <DatasetViewerComponent
                    file={file}
                    useUrl={switchStatus}
                    url={text}
                    setDatasetColumns={props.data.setDatasetColumns}
                    fileUploading={fileUploading}
                    setFileUploading={setFileUploading}
                    isValidCsvUrl={props.data.isValidCsvUrl}
                />
            </Flex>
        </ConfigProvider>
    );
};
